import { parseParamValue } from '@rcg/query-parser';
import { Search } from '@rcg/ui-components';
import { withEnableDisableScroller } from 'components/Scroller/context';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { useConfig } from 'hooks';
import { useCallback, useContext, useEffect, useState } from 'react';
import { BRAND_RCI } from '../../contexts/CruiseSearchContext/constants';
import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';
import TootipSearch from './TootipSearch';
import { CEL_KEYWORDS, NL_INPUT_ID, RCL_KEYWORDS, errorMessages, timeoutMs } from './const';
import { ErrorMessage, TooltipContainer } from './styles';
import { fetchNTL, fetchWithTimeout, getParamFromNL, isValidInput } from './utils';

function NLSearch() {
	const {
		brand,
		updateSearchSelecting,
		applyFilters,
		nlUserQuery,
		results,
		resetAllFilters,
		nlsValue,
		nlsPerformed,
		updateNls,
		naturalLanguage,
		searchSelecting,
		updateCurrentNls,
		nlsInput,
		updateNlsInput,
	} = useContext(CruiseSearchContext);
	const { valueSearch } = naturalLanguage || {};
	const { enableNaturalLanguageSearchVariations, enableNaturalLanguageSearch, enableGraphNlSearch } = useFeatureFlags();

	const [userInput, setUserInput] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const config = useConfig();

	const handleSearch = useCallback(
		async (searchValue, isQueryParameter) => {
			if (!isQueryParameter) {
				updateNls(true);
			}

			if (!searchValue || searchValue.toLowerCase() === userInput.toLowerCase()) return;

			setUserInput(searchValue);
			const userQuery = searchValue.toLowerCase().trim();
			setIsLoading(true);
			setError('');
			updateNlsInput(searchValue);

			let params = null;

			try {
				if (isValidInput(searchValue)) {
					if (enableGraphNlSearch) {
						updateSearchSelecting(userQuery, {}, applyFilters);
					} else {
						const response = await fetchWithTimeout(fetchNTL(userQuery, config), timeoutMs);
						if (response) {
							if (!response.completion_text) {
								setError(errorMessages.zeroResults);
								updateNlsInput('');
								return;
							}
							params = parseParamValue(response.completion_text);
							Object.keys(params).forEach((key) => {
								if (!Array.isArray(params[key])) params[key] = [params[key]];
							});
							const ntlParams = getParamFromNL(params, searchSelecting, enableNaturalLanguageSearch);
							updateCurrentNls(ntlParams);
							updateSearchSelecting(userQuery, ntlParams, applyFilters);
							updateNlsInput('');
						}
					}
				}
			} catch (error) {
				setError(error.message || errorMessages.generic);
			} finally {
				handleSearchFinally(params, userQuery);
			}
		},
		[applyFilters, brand, updateSearchSelecting, userInput, getParamFromNL],
	);

	const handleSearchFinally = (params, userQuery) => {
		if (nlsEnabled()) {
			updateNls(true);
			if (!params) {
				updateCurrentNls(null);
				updateSearchSelecting(userQuery, {}, applyFilters);
			}
		}
		setIsLoading(false);
		document.getElementById(NL_INPUT_ID)?.blur();
	};

	const nlsEnabled = () => {
		return nlsValue && !nlsPerformed;
	};

	if (nlsEnabled()) {
		handleSearch(nlsValue, true);
	}

	useEffect(() => {
		if (nlUserQuery && results?.noResults) {
			setError(errorMessages.zeroResults);
			resetAllFilters();
		}
	}, [nlUserQuery, results, resetAllFilters]);

	useEffect(() => {
		if (valueSearch !== '') {
			handleSearch(valueSearch);
		}
	}, [valueSearch]);

	return (
		<>
			<Search
				fullWidth={true}
				elevation={false}
				placeholders={enableNaturalLanguageSearchVariations}
				onClick={handleSearch}
				isLoading={isLoading}
				defaultValue={nlsValue || ''}
				keywords={brand === BRAND_RCI.code ? RCL_KEYWORDS : CEL_KEYWORDS}
				value={nlsInput}
			/>

			<TooltipContainer>
				{error && <ErrorMessage>{error}</ErrorMessage>}
				<TootipSearch />
			</TooltipContainer>
		</>
	);
}

export default withEnableDisableScroller(NLSearch);
